import React, { useState } from 'react';
import {
    Box, Paper, Typography, Button, TextField, InputAdornment,
    Divider, Stack, LinearProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const HARDCAP     = 1_000_000;
const STORAGE_KEY = 'ebonds_seed_raised';

const PresaleCard = () => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const isAdmin = new URLSearchParams(window.location.search).get('admin') === 'true';

    const [totalRaised, setTotalRaised] = useState(() => {
        try {
            const v = localStorage.getItem(STORAGE_KEY);
            return v ? parseFloat(v) : 0;
        } catch { return 0; }
    });
    const [adminInput, setAdminInput] = useState('');

    const handleAdminSave = () => {
        const val = parseFloat(adminInput);
        if (!isNaN(val) && val >= 0 && val <= HARDCAP) {
            localStorage.setItem(STORAGE_KEY, val.toString());
            setTotalRaised(val);
            setAdminInput('');
        }
    };

    const progressPct  = Math.min((totalRaised / HARDCAP) * 100, 100);
    const border        = isDark ? 'rgba(255,255,255,0.08)' : theme.palette.divider;
    const innerBg       = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(10,16,32,0.025)';
    const innerBorder   = isDark ? 'rgba(255,255,255,0.06)' : theme.palette.divider;
    const progressBg    = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,16,32,0.08)';
    const inputBg       = isDark ? '#05090f' : '#f7f9fc';

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

            {/* Admin Panel — visible at ?admin=true */}
            {isAdmin && (
                <Paper sx={{
                    p: 3, mb: 3,
                    border: '2px dashed rgba(210,157,92,0.5)',
                    bgcolor: isDark ? 'rgba(210,157,92,0.06)' : 'rgba(210,157,92,0.03)',
                }}>
                    <Typography variant="overline" color="primary.main" fontWeight={800} sx={{ display: 'block', mb: 1 }}>
                        ADMIN — SEED PROGRESS
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Current: ${totalRaised.toLocaleString()} / ${HARDCAP.toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter raised amount"
                            value={adminInput}
                            onChange={(e) => setAdminInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAdminSave(); }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                sx: { bgcolor: inputBg },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAdminSave}
                            sx={{ fontWeight: 700, whiteSpace: 'nowrap', px: 3 }}
                        >
                            Save
                        </Button>
                    </Stack>
                </Paper>
            )}

            {/* Main OTC Card */}
            <Paper sx={{ p: 5, bgcolor: 'background.paper', border: `1px solid ${border}` }}>

                {/* Progress bar */}
                {/* <Box sx={{ mb: 4 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>SEED PROGRESS</Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={700}>
                            ${totalRaised.toLocaleString()} / ${HARDCAP.toLocaleString()}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                            height: 8, borderRadius: 4,
                            bgcolor: progressBg,
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #d29d5c 0%, #e3b578 100%)',
                            },
                        }}
                    />
                </Box> */}

                <Typography variant="overline" color="text.secondary" fontWeight={700}>PRIVATE SEED ROUND</Typography>
                <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 1.5 }}>
                    OTC Allocation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                    Seed round allocations are handled directly via OTC. Reach out on Discord to secure your position and receive onboarding details.
                </Typography>

                {/* Terms box */}
                <Box sx={{ mb: 4, p: 4, bgcolor: innerBg, borderRadius: 2, border: `1px solid ${innerBorder}` }}>
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Min Allocation</Typography>
                            <Typography variant="body2" fontWeight={700} color="text.primary">$2,000 USDC</Typography>
                        </Stack>
                        <Divider />

                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Bonus Range</Typography>
                            <Typography variant="body2" fontWeight={600} color="primary.main">+10% — +30%</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Settlement</Typography>
                            <Typography variant="body2" fontWeight={600} color="text.primary">USDC on Arbitrum</Typography>
                        </Stack>
                    </Stack>
                </Box>

                {/* CTA */}
                <Button
                    fullWidth
                    variant="contained"
                    href="https://discord.gg/gnbsEU7kEF"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        py: 2.25, fontWeight: 700, mb: 2,
                        background: 'linear-gradient(45deg, #d29d5c, #e3b578)',
                        color: '#fff !important',
                    }}
                >
                    Contact on Discord
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                    DM us directly to initiate your OTC allocation
                </Typography>
            </Paper>
        </Box>
    );
};

export default PresaleCard;
